import PropTypes from "prop-types";
import { Dialog } from "@mui/material";
import { useTranslation } from "react-i18next";
import getStyled from "../../../hooks/getStyled";
import SFIconButton from "../../button/iconButton";
import { SFStack } from "../../grid";
import { SFCloseIcon } from "../../icons";
import SFTypography from "../../typography";

const { styled } = getStyled();

const StyledDialog = styled(Dialog, {
  shouldForwardProp: (prop) => prop !== "disableGutters",
})(({ theme, fullScreen, disableGutters }) => ({
  ".MuiPaper-root": {
    width: "100%",
    ...(fullScreen && { height: "100%" }),
    ...(!disableGutters && {
      padding: theme.spacing(5),
      paddingTop: theme.spacing(3),
    }),
    overflow: "hidden",
  },
}));

const StyledContainer = styled("div")(() => ({
  maxWidth: 550,
}));

const SFIconButtonStyled = styled(SFIconButton)(() => ({
  borderRadius: "50%",
}));

const ActionsWrapper = styled(SFStack, {
  shouldForwardProp: (prop) => prop !== "disableGutters",
})(({ disableGutters }) => ({
  ...(!disableGutters && {
    marginRight: "-20px",
  }),
}));

const HeaderWrapper = styled(SFStack, {
  shouldForwardProp: (prop) =>
    prop !== "disableGutters" && prop !== "darkHeader",
})(({ theme, disableGutters, darkHeader }) => ({
  ...(disableGutters && {
    padding: `${theme.spacing(1.5)} ${theme.spacing(2)}`,
  }),
  ...(darkHeader && {
    backgroundColor: theme.palette.grey450.main,
  }),
  boxShadow: "0px 2px 8px 0px rgba(116, 116, 128, 0.08)",
}));

const Title = ({
  header,
  actions,
  closeButton,
  onClose,
  subHeader,
  disableGutters,
  darkHeader,
}) => {
  const { t } = useTranslation();
  return (
    <HeaderWrapper
      darkHeader={darkHeader}
      direction="column"
      disableGutters={disableGutters}
    >
      <SFStack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
      >
        {header && (
          <SFTypography
            {...(darkHeader && { color: "white" })}
            variant="cardTitle"
          >
            {header}
          </SFTypography>
        )}
        <ActionsWrapper
          direction="row"
          alignItems="flex-start"
          spacing={2}
          disableGutters={disableGutters}
        >
          {actions && actions}
          {closeButton || (
            <SFIconButtonStyled
              aria-label={t("components.dialog.closeButtonAriaLabel")}
              onClick={onClose}
            >
              <SFCloseIcon />
            </SFIconButtonStyled>
          )}
        </ActionsWrapper>
      </SFStack>
      <StyledContainer>{subHeader && subHeader}</StyledContainer>
    </HeaderWrapper>
  );
};

const SFFullscreenDialog = ({
  open,
  onClose,
  actions,
  maxWidth,
  fullWidth,
  children,
  header,
  subHeader,
  fullScreen,
  closeButton,
  disableGutters,
  darkHeader = false,
}) => {
  return (
    <StyledDialog
      aria-labelledby="dialog-title"
      open={open}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      fullScreen={fullScreen}
      onClose={onClose}
      disableGutters={disableGutters}
    >
      <SFStack spacing={disableGutters ? 0 : 3} sx={{ height: "100%" }}>
        <Title
          header={header}
          actions={actions}
          closeButton={closeButton}
          onClose={onClose}
          subHeader={subHeader}
          disableGutters={disableGutters}
          darkHeader={darkHeader}
        />
        {children}
      </SFStack>
    </StyledDialog>
  );
};

SFFullscreenDialog.defaultProps = {
  actions: null,
  maxWidth: "lg",
  fullWidth: true,
  header: null,
  subHeader: null,
  disableGutters: false,
  closeButton: null,
  darkHeader: false,
};

SFFullscreenDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  actions: PropTypes.element,
  maxWidth: PropTypes.oneOf(["sm", "md", "lg", "xl"]),
  fullWidth: PropTypes.bool,
  children: PropTypes.element.isRequired,
  header: PropTypes.element,
  subHeader: PropTypes.element,
};

export default SFFullscreenDialog;
